import { FileCopyOutlined, InfoOutlined, SettingsOutlined, Terminal } from '@mui/icons-material';
import { Alert, Box, Checkbox, FormControlLabel, Stack, Tooltip, Typography } from '@mui/material';
import React from 'react';

import { Button } from 'new/elements/Button';
import { TextField } from 'new/elements/TextField';
import styles from 'new/modules/settings/capsules/components/HelmCommandPreview/HelmCommandPreview.module.scss';
import {
  useHelmCommandPreview,
  type UseHelmCommandPreviewProps,
} from 'new/modules/settings/capsules/components/HelmCommandPreview/hooks/useHelmCommandPreview';

function getCommandTokenClassName(token: string): string | undefined {
  if (token === 'helm' || token === 'upgrade') return styles.commandToken;
  if (token.startsWith('--')) return styles.flagToken;
  if (token.endsWith('=')) return styles.keyToken;
  if (token.startsWith("'")) return styles.valueToken;
  if (token === '\\') return styles.continuationToken;

  return undefined;
}

function renderHighlightedCommand(command: string) {
  return command.split('\n').map((line, lineIndex, lines) => {
    const tokens = line.split(/(\s+|\\$|--[^\s=]+|[A-Za-z0-9_.-]+=|'[^']*(?:'\\''[^']*)*')/g);

    return (
      <React.Fragment key={`line-${lineIndex}`}>
        {tokens.map((token, tokenIndex) => {
          if (token.length === 0) return null;

          const tokenClassName = getCommandTokenClassName(token);
          if (!tokenClassName) return token;

          return (
            <span key={`${lineIndex}-${tokenIndex}`} className={tokenClassName}>
              {token}
            </span>
          );
        })}
        {lineIndex < lines.length - 1 ? '\n' : null}
      </React.Fragment>
    );
  });
}

export function HelmCommandPreview(props: UseHelmCommandPreviewProps) {
  const {
    fields,
    displayCommand,
    diagnosticsDumpsEnabled,
    isHelmCommandCopied,
    copyButtonLabel,
    onDiagnosticsDumpsEnabledChange,
    handleCopyCommand,
  } = useHelmCommandPreview(props);

  const copyButtonClassName = isHelmCommandCopied ? styles.copiedButton : styles.copyButton;

  return (
    <>
      <Box className={styles.valuesContainer}>
        <div className={styles.valuesHeader}>
          <span className={styles.valuesHeaderLeft}>
            <span className={styles.valuesHeaderIcon} aria-hidden>
              <SettingsOutlined fontSize="small" />
            </span>
            <Typography variant="subtitle1" className={styles.valuesHeaderTitle} component="span">
              Helm Deployment Values
            </Typography>
          </span>
        </div>
        <div className={styles.diagnosticsSection}>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={diagnosticsDumpsEnabled}
                onChange={onDiagnosticsDumpsEnabledChange}
                inputProps={{ 'aria-label': 'Enable diagnostic dumps' }}
              />
            }
            label={
              <Stack direction="row" alignItems="center" gap={0.75}>
                <Typography variant="body2">Enable diagnostic dumps</Typography>
                <Tooltip
                  title="Highly recommended. Without this, heap and thread dumps cannot be fetched for future debugging."
                  placement="top"
                  arrow
                >
                  <InfoOutlined fontSize="small" className={styles.diagnosticsInfoIcon} />
                </Tooltip>
              </Stack>
            }
          />
          {diagnosticsDumpsEnabled ? (
            <div className={styles.dynamicValuesGrid}>
              {fields.map((field) => (
                <TextField
                  key={field.key}
                  fullWidth
                  label={field.label}
                  inputProps={{ 'aria-label': field.label }}
                  value={field.value}
                  onChange={(event) => field.onChange(event.target.value)}
                />
              ))}
            </div>
          ) : null}
          {!diagnosticsDumpsEnabled ? (
            <Alert severity="warning" variant="outlined">
              Without diagnostic dumps enabled, heap and thread dumps cannot be fetched for future
              debugging purposes.
            </Alert>
          ) : null}
        </div>
      </Box>
      <Box className={styles.container}>
        <Box className={styles.header}>
          <Box className={styles.headerLeft}>
            <Box className={styles.windowDots} aria-hidden>
              <span className={styles.windowDotRed} />
              <span className={styles.windowDotYellow} />
              <span className={styles.windowDotGreen} />
            </Box>
            <div className={styles.headerTitleGroup}>
              <Stack direction="row" className={styles.iconPair} aria-hidden>
                <Terminal className={styles.headerIcon} />
              </Stack>
              <Typography variant="subtitle1" component="h3" className={styles.headerTitle}>
                HELM COMMAND
              </Typography>
            </div>
          </Box>
          <Stack direction="row" className={styles.headerActions}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<FileCopyOutlined />}
              onClick={handleCopyCommand}
              className={copyButtonClassName}
            >
              {copyButtonLabel}
            </Button>
          </Stack>
        </Box>
        <Box className={styles.commandContainer}>
          <Typography component="pre" className={styles.commandText} data-testid="helm-command">
            {renderHighlightedCommand(displayCommand)}
          </Typography>
        </Box>
      </Box>
    </>
  );
}

HelmCommandPreview.displayName = 'HelmCommandPreview';
